/**
 * Purchase Order Service
 * Handles API requests related to purchase orders
 */
import { createService } from './serviceFactory';

// Define the base URL for purchase order service
const baseUrl = '/api/supplier/purchase-order';

// Create base service
const baseService = createService(baseUrl);

const purchaseOrderService = {
  /**
   * Search purchase orders with filters
   * @param {Object} searchParams - Search parameters including page, pageSize, etc.
   * @returns {Promise} - Response with purchase order items and status counts
   */
  searchPurchaseOrders(searchParams = {}) {
    // Set default values for pagination if not provided
    const params = {
      // UI sends 1-based page index; serviceFactory.search converts to API 0-based.
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 10,
      searchText: searchParams.searchText || '',
      sorting: searchParams.sorting || 'creationTime desc',
    };
    
    // Handle trackingStatus as a number or array
    if (searchParams.trackingStatus !== undefined) {
      params.trackingStatus = searchParams.trackingStatus;
    }
    
    // Handle additional filters
    if (searchParams.trackingStatusDisplay) {
      params.trackingStatus = searchParams.trackingStatusDisplay;
    }

    if (searchParams.vesprocStatusId) {
      params.vesprocStatusId = searchParams.vesprocStatusId;
    }
    if (searchParams.purchaseOrderNo) {
      params.purchaseOrderNo = searchParams.purchaseOrderNo;
    }
    
    if (searchParams.supplierCode) {
      params.supplierCode = searchParams.supplierCode;
    }
    
    if (searchParams.dateFrom) {
      params.dateFrom = searchParams.dateFrom;
    }
    
    if (searchParams.dateTo) {
      params.dateTo = searchParams.dateTo;
    }
    
    return baseService.search(params);
  },
  
  /**
   * Get a single purchase order by ID
   * @param {string} id - Purchase Order ID
   * @returns {Promise} - Response with purchase order details
   */
  getPurchaseOrder(id) {
    return baseService.getById(id);
  },
  
  /**
   * Submit a purchase order response
   * @param {string} id - Purchase Order ID
   * @param {Object} data - Purchase order data
   * @returns {Promise} - Response
   */
  submitPurchaseOrder(id, data) {
    return baseService.client
      .post(`${baseUrl}/${id}/submit`, data)
      .then(response => response.data);
  },
  
  /**
   * Update purchase order
   * @param {string} id - Purchase Order ID
   * @param {Object} data - Purchase order data
   * @returns {Promise} - Response
   */
  updatePurchaseOrder(id, data) {
    return baseService.update(id, data);
  },
  
  /**
   * Delete purchase order
   * @param {string} id - Purchase Order ID
   * @returns {Promise} - Response
   */
  deletePurchaseOrder(id) {
    return baseService.delete(id);
  },
  
  /**
   * Decline purchase order
   * @param {string} id - Purchase Order ID
   * @param {Object} data - Decline reason data
   * @returns {Promise} - Response
   */
  declinePurchaseOrder(id, data) {
    return baseService.client
      .post(`${baseUrl}/${id}/decline`, data)
      .then(response => response.data);
  },
  
  /**
   * Export purchase order to Excel
   * @param {string} id - Purchase Order ID
   * @returns {Promise} - Response with file data
   */
  exportPurchaseOrder(id) {
    return baseService.client
      .post(`${baseUrl}/${id}/export`, null, { responseType: 'blob' })
      .then(response => response.data);
  },
  
  /**
   * Get purchase order items
   * @param {string} id - Purchase Order ID
   * @returns {Promise} - Response with items
   */
  getPurchaseOrderItems(id) {
    return baseService.client
      .get(`${baseUrl}/${id}/items`)
      .then(response => response.data);
  },
  
  /**
   * Update purchase order items
   * @param {string} id - Purchase Order ID
   * @param {Array} items - Purchase order items
   * @returns {Promise} - Response
   */
  updatePurchaseOrderItems(id, items) {
    return baseService.client
      .post(`${baseUrl}/${id}/items`, { items })
      .then(response => response.data);
  },
  
  /**
   * Get purchase order attachments
   * @param {string} id - Purchase Order ID
   * @returns {Promise} - Response with attachments
   */
  getPurchaseOrderAttachments(id) {
    return baseService.client
      .get(`${baseUrl}/${id}/attachments`)
      .then(response => response.data);
  },
  
  /**
   * Get purchase order comments
   * @param {string} id - Purchase Order ID
   * @returns {Promise} - Response with comments
   */
  getPurchaseOrderComments(id) {
    return baseService.client
      .get(`${baseUrl}/${id}/comments`)
      .then(response => response.data);
  },
  
  /**
   * Add comment to purchase order
   * @param {string} id - Purchase Order ID
   * @param {Object} comment - Comment data
   * @returns {Promise} - Response
   */
  addPurchaseOrderComment(id, comment) {
    return baseService.client
      .post(`${baseUrl}/${id}/comments`, comment)
      .then(response => response.data);
  },

  updatePurchaseOrderStatus(id, trackingStatusDisplay) {
    return baseService.client
      .post(`${baseUrl}/${id}/change-status`, { trackingStatus: trackingStatusDisplay })
      .then(response => response.data);
  }
};

export default purchaseOrderService;
